import { apiSend } from './api.js';
import { auth } from './actor.js';



export interface RefParty {
  code: string;
  name: string;
  isSeller: boolean;
  isBuyer: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
  isSubAgent: boolean;
  countryCode: string | null;
}

export interface RefCode {
  code: string;
  name: string;
}

export interface RefPort extends RefCode {
  countryCode: string | null;
  mode: string;
}

export interface BrokerageRefs {
  parties: RefParty[];
  uoms: Array<RefCode & { decimals: number }>;
  ports: RefPort[];
  salespersons: RefCode[];
  products: RefCode[];
  nextDealNo: string | null;
  enums: {
    kinds: string[];
    currencies: string[];
    incoterms: string[];
    commissionBases: string[];
    accrualPolicies: string[];
    availabilityStatuses: string[];
    paymentMethods: string[];
    transitions: string[];
  };
}


export interface DealLinePayload {
  productName: string;
  quantity: string;
  uomCode: string;
  unitRate: string;
  commissionRate?: string | null;
  narration?: string | null;
}

export interface DealSplitPayload {
  subAgentCode?: string | null;
  salespersonCode?: string | null;
  sharePct?: string | null;
  fixedAmountBase?: string | null;
}

export interface DealPayload {
  dealNo?: string | null;
  dealDate: string;
  kind: 'LOCAL' | 'IMPORT';
  partyACode: string;
  partyBCode: string;
  currency: string;
  fxRate: string;
  commissionBasis: string;
  commissionRate?: string | null;
  commissionAmount?: string | null;
  commissionCurrency: string;
  commissionFxRate?: string | null;
  accrualPolicy: string;
  branchCode?: string | null;
  narration?: string | null;
  lines: DealLinePayload[];
  splits: DealSplitPayload[];
}

export interface SavedDeal {
  dealNo: string;
  status: string;
  dealValueBase: string;
  commissionBase: string;
  lineCount: number;
  message: string;
}

export interface DealTransition {
  dealNo: string;
  status: string;
  message: string;
}

export interface SettlementResult {
  dealNo: string;
  seqNo: number;
  confirmedBase: string;
  balanceBase: string;
  releasedBase: string;
  status: string;
  message: string;
}

export interface CommissionInvoiceResult {
  dealNo: string;
  invoiceNo: string;
  voucherNo: string;
  amountBase: string;
  receivableAccount: string;
  incomeAccount: string;
  status: string;
  message: string;
}

export interface CommissionReceiptResult {
  invoiceNo: string;
  voucherNo: string;
  netBase: string;
  whtBase: string;
  fxDiffBase: string;
  currency: string;
  estimatedRate: string;
  actualRate: string;
  message: string;
}

export interface AvailabilityRow {
  id: string;
  product: string;
  grade: string | null;
  origin: string | null;
  supplier: string;
  supplierCode: string;
  rate: string | null;
  currency: string;
  rateUom: string | null;
  rateUomCode: string | null;
  portOfLoading: string | null;
  portOfLoadingCode: string | null;
  incoterm: string | null;
  leadTimeDays: number | null;
  minQtyBase: string | null;
  maxQtyBase: string | null;
  notes: string | null;
  lastConfirmed: string | null;
  status: string;
}

export interface AvailabilityPayload {
  id?: string | null;
  productName: string;
  supplierCode: string;
  grade?: string | null;
  originCountry?: string | null;
  indicativeRate?: string | null;
  currency?: string;
  rateUomCode?: string | null;
  status?: string;
  portOfLoadingCode?: string | null;
  incoterm?: string | null;
  leadTimeDays?: number | null;
  minQtyBase?: string | null;
  maxQtyBase?: string | null;
  notes?: string | null;
  confirmNow?: boolean;
}


const deal = (no: string, suffix = '') => `/deals/${encodeURIComponent(no)}${suffix}`;

export function saveDeal(payload: DealPayload): Promise<SavedDeal> {
  return apiSend<SavedDeal>('POST', '/deals', payload, auth());
}

export function deleteDeal(dealNo: string): Promise<DealTransition> {
  return apiSend<DealTransition>('DELETE', deal(dealNo), undefined, auth());
}

export function advanceDeal(
  dealNo: string,
  status: string,
  extra: { date?: string; reason?: string } = {},
): Promise<DealTransition> {
  return apiSend<DealTransition>('POST', deal(dealNo, '/status'), { status, ...extra }, auth());
}

export function recordSettlement(
  dealNo: string,
  payload: {
    settlementDate: string;
    currency: string;
    fxRate: string;
    amount: string;
    paymentMethod?: string | null;
    proofReference?: string | null;
    confirmed: boolean;
    note?: string | null;
  },
): Promise<SettlementResult> {
  return apiSend<SettlementResult>('POST', deal(dealNo, '/settlements'), payload, auth());
}

export function raiseCommissionInvoice(
  dealNo: string,
  payload: { invoiceDate?: string; dueDate?: string | null; whtPct?: string; narration?: string },
): Promise<CommissionInvoiceResult> {
  return apiSend<CommissionInvoiceResult>(
    'POST',
    deal(dealNo, '/commission-invoice'),
    payload,
    auth(),
  );
}

export function recordCommissionReceipt(
  invoiceNo: string,
  payload: {
    receiptDate?: string;
    bankAccountCode?: string | null;
    fxRate?: string;
    narration?: string;
  },
): Promise<CommissionReceiptResult> {
  return apiSend<CommissionReceiptResult>(
    'POST',
    `/commission-invoices/${encodeURIComponent(invoiceNo)}/receipt`,
    payload,
    auth(),
  );
}

export function saveAvailability(
  payload: AvailabilityPayload,
): Promise<{ id: string; message: string }> {
  return apiSend<{ id: string; message: string }>('POST', '/product-availability', payload, auth());
}

export function deleteAvailability(id: string): Promise<{ id: string; message: string }> {
  return apiSend<{ id: string; message: string }>(
    'DELETE',
    `/product-availability/${encodeURIComponent(id)}`,
    undefined,
    auth(),
  );
}
